import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { matchingAPI } from '../api/api';
import MatchScoreBadge, { MatchScoreBreakdown } from './MatchScoreBadge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Sparkles, MapPin, DollarSign, Clock, ChevronRight, 
  RefreshCw, Eye, Filter, ChevronDown, ChevronUp
} from 'lucide-react';

const JobsForYou = ({ compact = false }) => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minScore, setMinScore] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    fetchRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [minScore]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await matchingAPI.getJobsForWorker({ min_score: minScore, limit: compact ? 5 : 20 });
      setRecommendations(response.data.jobs || []);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Unable to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg" data-testid="jobs-for-you-loading">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Jobs For You
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-500">Finding your best matches...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Jobs For You
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-4">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchRecommendations} className="w-full">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg" data-testid="jobs-for-you">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Jobs For You
            {recommendations.length > 0 && (
              <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
                {recommendations.length} matches
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {!compact && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="text-gray-500"
              >
                <Filter className="h-4 w-4 mr-1" />
                Filter
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchRecommendations}
              className="text-gray-500"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        {showFilters && !compact && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <label className="text-sm text-gray-600">
              Minimum Match Score: <span className="font-semibold text-blue-600">{minScore}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="80"
              step="10"
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              className="w-full mt-2 accent-blue-500"
              data-testid="min-score-filter"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>All</span>
              <span>40%</span>
              <span>80%+</span>
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="space-y-3">
        {recommendations.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-gray-500">No matching jobs found.</p>
            <p className="text-sm text-gray-400 mt-1">Try updating your skills in your profile for better matches.</p>
          </div>
        ) : (
          <>
            {recommendations.map((job) => (
              <div
                key={job.job_id}
                className={`p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${
                  job.match_level === 'excellent' ? 'border-green-200 bg-green-50/50' :
                  job.match_level === 'good' ? 'border-blue-200 bg-blue-50/50' :
                  'border-gray-200 bg-white'
                }`}
                onClick={() => navigate(`/job/${job.job_id}`)}
                data-testid={`recommended-job-${job.job_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900 truncate">{job.title}</h4>
                      <MatchScoreBadge score={job.match_score} size="sm" showLabel={false} />
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{job.company}</p>
                    
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                      </span>
                      {job.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {job.duration}
                        </span>
                      )}
                    </div>
                    
                    {/* Skills */}
                    {job.required_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {job.required_skills.slice(0, 4).map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="text-[10px] bg-gray-100">
                            {skill}
                          </Badge>
                        ))}
                        {job.required_skills.length > 4 && (
                          <Badge variant="secondary" className="text-[10px] bg-gray-100">
                            +{job.required_skills.length - 4}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedJob(expandedJob === job.job_id ? null : job.job_id);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {expandedJob === job.job_id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/job/${job.job_id}`);
                      }}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
                
                {/* Expanded breakdown */}
                {expandedJob === job.job_id && job.breakdown && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <MatchScoreBreakdown breakdown={job.breakdown} />
                  </div>
                )}
              </div>
            ))}
            
            {!compact && recommendations.length >= 20 && (
              <p className="text-center text-sm text-gray-400 pt-2">
                Showing top 20 matches
              </p>
            )}
            
            {compact && recommendations.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/browse-jobs?view=recommended')}
                className="w-full mt-2"
              >
                View All Recommendations
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default JobsForYou;
